import StoreScopeTag from "@/components/partner/StoreScopeTag";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { QrCode, Users, Wallet, Star, Coins } from "lucide-react";
import { sharedApi } from "@/lib/api";

/**
 * Iter-361 — vendor dashboard stats strip.
 * Iter-412 §4 — reformatted per spec: each tile shows the ALL-TIME big
 * number + a "+N / 30d" small line (period toggle retired), and a new
 * ⭐ Reviews tile (all_time.reviews_posted). Stats are store-scoped via
 * the X-Store-Id header (injected by the sharedApi interceptor).
 */

const OPTS = {
  headers: { "X-App-Platform": "nz_classified" },
  params: { platform: "nz_classified" },
};

function money(n) {
  const v = Number(n);
  return Number.isFinite(v) ? `$${v.toFixed(2)}` : "$0.00";
}

export default function VendorStatsStrip() {
  const q = useQuery({
    queryKey: ["vendor-stats"],
    retry: 0,
    staleTime: 60_000,
    queryFn: async () =>
      (await sharedApi.get("/classifieds/engagement/vendors/me/stats", OPTS)).data,
  });

  // 404 = no registered vendor; any failure just hides the strip.
  if (q.isError || (!q.isLoading && !q.data)) return null;

  const all = q.data?.all_time || {};
  const m30 = q.data?.last_30_days || {};
  const followers = Number(q.data?.followers) || 0;
  const allZero =
    !q.isLoading &&
    ["sessions", "paid_sessions", "unique_customers", "revenue_nzd", "reviews_posted", "points_redeemed"].every(
      (k) => !Number(all[k])
    );

  const d30 = (k) => `+${Number(m30[k]) || 0} / 30d`;

  return (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 text-left" data-testid="vendor-stats-strip">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
        Till stats
        <StoreScopeTag />
      </div>

      {q.isLoading ? (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : allZero ? (
        <p className="mt-3 text-sm text-slate-500" data-testid="vendor-stats-empty">
          Your till stats will appear after your first customer scan.
        </p>
      ) : (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <Tile
            icon={QrCode}
            label="Visits"
            value={Number(all.sessions) || 0}
            sub={d30("sessions")}
            testid="vendor-stats-visits"
          />
          <Tile
            icon={Users}
            label="Customers"
            value={Number(all.unique_customers) || 0}
            sub={`${d30("unique_customers")} · ${followers} follower${followers === 1 ? "" : "s"}`}
            testid="vendor-stats-customers"
          />
          <Tile
            icon={Wallet}
            label="Revenue"
            value={money(all.revenue_nzd)}
            sub={`+${money(m30.revenue_nzd)} / 30d`}
            testid="vendor-stats-revenue"
          />
          <Tile
            icon={Star}
            label="Reviews"
            value={Number(all.reviews_posted) || 0}
            sub={d30("reviews_posted")}
            testid="vendor-stats-reviews"
          />
          <Tile
            icon={Coins}
            label="Points used"
            value={`${Number(all.points_redeemed) || 0} pts`}
            sub={`${d30("points_redeemed")} · = ${money(all.points_discount_nzd)}, funded by Allsale`}
            subClass="text-emerald-700"
            testid="vendor-stats-points"
          />
        </div>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, sub, subClass = "text-slate-400", testid }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="mt-1 font-display text-xl font-black text-slate-900">{value}</div>
      {sub && <div className={`mt-0.5 text-[11px] font-semibold ${subClass}`}>{sub}</div>}
    </div>
  );
}
